import React, { useMemo, useState, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';

import { Box } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { uniq } from 'lodash-es';

import { ShortName } from '@components';
import { IconButtonV2, Loader, ButtonV2 } from 'components/common/ui';
import { useTeamsOperations, useToggleModal } from '@hooks';
import { generateShortId, showError, getShortName } from '@utils';
import AttachmentList from 'components/emailTemplates/EmailTemplatesForm/AttachmentList/AttachmentList';
import { modules, allowedFileTypes, MAX_FILE_SIZE } from './constants';
import { styles } from './emailReply.style';
import EmailChipsInput from '../EmailChipsInput/EmailChipsInput';
import 'react-quill/dist/quill.snow.css';
import { REPLY_CONTAINER_ID } from '../EmailViewDetail/EmailViewDetail';
import { extendedShortNameStyles } from '../../common/ShortName/ShortName';
import EmailTemplatesModal from '../EmailTemplatesModal/EmailTemplatesModal';
import EmailReplySignatures from './EmailReplySignatures/EmailReplySignatures';
import { CONNECT_MAIL_TO_ENTITY_ERROR } from '../constants';

const ReactQuill = dynamic(() => import('react-quill'), {
  ssr: false,
});
export const drawIconSvg = `<svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall css-bzdzu9" focusable="false" aria-hidden="true" viewBox="0 0 24 24" aria-label="fontSize small"><path d="m18.85 10.39 1.06-1.06c.78-.78.78-2.05 0-2.83L18.5 5.09c-.78-.78-2.05-.78-2.83 0l-1.06 1.06zm-5.66-2.83L4 16.76V21h4.24l9.19-9.19zM19 17.5c0 2.19-2.54 3.5-5 3.5-.55 0-1-.45-1-1s.45-1 1-1c1.54 0 3-.73 3-1.5 0-.47-.48-.87-1.23-1.2l1.48-1.48c1.07.63 1.75 1.47 1.75 2.68M4.58 13.35C3.61 12.79 3 12.06 3 11c0-1.8 1.89-2.63 3.56-3.36C7.59 7.18 9 6.56 9 6c0-.41-.78-1-2-1-1.26 0-1.8.61-1.83.64-.35.41-.98.46-1.4.12-.41-.34-.49-.95-.15-1.38C3.73 4.24 4.76 3 7 3s4 1.32 4 3c0 1.87-1.93 2.72-3.64 3.47C6.42 9.88 5 10.5 5 11c0 .31.43.6 1.07.86z"></path></svg>`;

const EmailReply = ({
  user,
  email,
  agent,
  isOpen,
  loading,
  isForward = false,
  hasConnectedEntity,
  onSubmit,
  onToggleReply,
  onCloseReply,
}) => {
  const quillRef = useRef(null);
  const { teams } = useTeamsOperations();
  const {
    isOpen: isTemplatesModalOpen,
    handleClose: handleCloseTemplatesModal,
    handleOpen: handleOpenTemplatesModal,
  } = useToggleModal(false);
  const emailAddressesToReceive = useMemo(() => {
    if (isForward) {
      return [];
    }
    if (email?.from?.emailAddress === agent?.email) {
      const receiver = email?.to.find(
        (item) => item.emailAddress !== agent.email
      );

      return receiver ? [receiver.emailAddress] : [];
    }
    if (email.mailboxAddress === email?.from?.emailAddress) {
      return email?.to.map((item) => item.emailAddress);
    }
    return email?.from?.emailAddress ? [email?.from?.emailAddress] : [];
  }, [email, agent, isForward]);

  const [receivers, setReceivers] = useState(emailAddressesToReceive);
  const [ccReceivers, setCcReceivers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [initialReceiver, setInitialReceiver] = useState(
    emailAddressesToReceive
  );
  const [hasCc, setHasCc] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const [editorValue, setEditorValue] = useState('');
  const [signature, setSignature] = useState(agent?.emailSignature);

  const fileInputRef = useRef(null);
  const [signaturePopoverOpen, setSignaturePopoverOpen] = useState(false);

  const quillModules = useMemo(
    () => ({
      ...modules,
      toolbar: {
        container: modules.toolbar,
        handlers: {
          attachment: () => {
            fileInputRef.current?.click();
          },
          insertTemplate: () => {
            handleOpenTemplatesModal();
          },
          signatureAction: () => {
            // Open popover
            setSignaturePopoverOpen(true);
          },
        },
      },
    }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    let mounted = true;

    const registerIcon = async () => {
      const RQ = (await import('react-quill')).default;

      if (!mounted || !RQ?.Quill) return;

      const icons = RQ.Quill.import('ui/icons');

      icons.signatureAction = drawIconSvg;
    };

    registerIcon();

    return () => {
      mounted = false;
    };
  }, []);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const allowedExtensions = allowedFileTypes.accept
      .split(',')
      .map((ext) => ext.trim().toLowerCase().replace(/^\./, ''));

    const oversizedFiles = [];
    const unsupportedFiles = [];
    const validFiles = [];

    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file);
        return;
      }

      const fileName = file.name.toLowerCase();
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
        unsupportedFiles.push(file);
        return;
      }
      const extension = fileName.substring(lastDotIndex + 1);
      if (!allowedExtensions.includes(extension)) {
        unsupportedFiles.push(file);
        return;
      }
      validFiles.push(file);
    });

    if (oversizedFiles.length > 0) {
      showError(allowedFileTypes.sizeError);
      resetFileInput();
      return;
    }

    if (unsupportedFiles.length > 0) {
      showError(allowedFileTypes.typeError);
      resetFileInput();
      return;
    }

    const newAttachments = validFiles.map((file) => ({
      id: generateShortId(),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
    resetFileInput();
  };

  const handleRemoveAttachment = (attachment) => {
    setAttachments((prev) => prev.filter((att) => att.id !== attachment.id));
  };

  const handleTextChange = (value) => {
    if (loading) return;
    setEditorValue(value);
  };

  const handleOnSend = async () => {
    if (onSubmit) {
      await onSubmit({
        body: `${editorValue} <br/><br/>${signature}`,
        receivers,
        ccReceivers,
        attachments: attachments
          .filter((attachment) => attachment.file)
          .map((attachment) => attachment.file),
        // send existing attachment ids from the template email
        attachmentIds: attachments
          .filter((attachment) => !attachment.file)
          .map((attachment) => attachment.id),
        templateId: selectedTemplate?.template || null,
        templateLang: selectedTemplate?.language || null,
      });
    }
  };

  const handleClose = () => {
    onCloseReply();
    setEditorValue('');
    setSignature(agent?.emailSignature);
    setSelectedTemplate(null);
    setAttachments([]);
    setCcReceivers([]);
    setHasCc(false);
  };

  const handleUseCc = () => {
    setHasCc(true);
  };

  const handleReplyToAll = () => {
    if (!hasConnectedEntity) {
      showError(CONNECT_MAIL_TO_ENTITY_ERROR);
      return;
    }
    const allReceivers = uniq([
      email?.from?.emailAddress,
      ...email.to.map((item) => item.emailAddress),
    ]);
    if (email.ccMails?.length) {
      setHasCc(true);
      setCcReceivers(email.ccMails.map((item) => item.emailAddress));
    }
    onToggleReply();
    setInitialReceiver(allReceivers);
    setReceivers(allReceivers);
  };

  const handleReply = () => {
    if (!hasConnectedEntity) {
      showError(CONNECT_MAIL_TO_ENTITY_ERROR);
      return;
    }
    onToggleReply();
    setInitialReceiver(emailAddressesToReceive);
    setReceivers(emailAddressesToReceive);
  };

  const handleForward = () => {
    if (!hasConnectedEntity) {
      showError(CONNECT_MAIL_TO_ENTITY_ERROR);
      return;
    }
    onToggleReply();
    setInitialReceiver([]);
    setReceivers([]);
  };

  const handleInsertTemplateSubmit = (template = {}) => {
    setSelectedTemplate(template);
    handleCloseTemplatesModal();

    setEditorValue(template.body);
    setAttachments((prev) => [
      ...prev.filter((att) => att.file),
      ...template.attachments,
    ]);
  };

  const handleChangeSignature = (newValue) => {
    if (newValue && quillRef.current) {
      setSignature(newValue.value);
      setSignaturePopoverOpen(false);
    }
  };

  const shortName = getShortName(user);

  return (
    <>
      {!isOpen && (
        <Box sx={styles.buttonContainer}>
          <ButtonV2 onClick={handleReply} loading={loading}>
            Reply
          </ButtonV2>
          <ButtonV2 onClick={handleReplyToAll} loading={loading}>
            Reply to all
          </ButtonV2>
          <ButtonV2 onClick={handleForward} loading={loading} disabled>
            Forward
          </ButtonV2>
        </Box>
      )}
      <Box id={REPLY_CONTAINER_ID}>
        {loading && <Loader />}
        {isOpen && (
          <Box sx={styles.wrapper}>
            <ShortName styles={extendedShortNameStyles}>{shortName}</ShortName>
            <Box sx={styles.replyWrapper}>
              <Box>
                <Box sx={styles.receiversWrapper}>
                  <Box sx={styles.receivers}>
                    To:
                    <EmailChipsInput
                      autoFocus
                      initialValue={isForward ? [] : initialReceiver}
                      onEmailsChange={setReceivers}
                    />
                  </Box>
                  {!hasCc && (
                    <Box sx={styles.cc}>
                      <ButtonV2 variant="text" onClick={handleUseCc}>
                        Cc
                      </ButtonV2>
                    </Box>
                  )}
                </Box>

                {hasCc && (
                  <Box sx={styles.receivers}>
                    Cc:
                    <EmailChipsInput
                      initialValue={ccReceivers}
                      onEmailsChange={setCcReceivers}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={styles.editorContainer}>
                <ReactQuill
                  ref={quillRef}
                  value={editorValue}
                  modules={quillModules}
                  onChange={handleTextChange}
                  readOnly={loading}
                />
                <Box
                  sx={styles.signatureWrapper}
                  dangerouslySetInnerHTML={{
                    // eslint-disable-line react/no-danger
                    __html: signature,
                  }}
                />
                <EmailReplySignatures
                  teams={teams}
                  agent={agent}
                  isOpen={signaturePopoverOpen}
                  onClose={() => setSignaturePopoverOpen(false)}
                  onChange={handleChangeSignature}
                />
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={allowedFileTypes.accept}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {attachments.length > 0 && (
                <AttachmentList
                  attachments={attachments}
                  onDelete={handleRemoveAttachment}
                />
              )}

              <Box sx={styles.footer}>
                <Box sx={styles.toolbar}>
                  <ButtonV2 onClick={handleOnSend} loading={loading}>
                    Send
                  </ButtonV2>
                </Box>
                <IconButtonV2 onClick={handleClose}>
                  <DeleteIcon
                    data-testid="DeleteIcon"
                    color="white"
                    fontSize="small"
                  />
                </IconButtonV2>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {isTemplatesModalOpen && (
        <EmailTemplatesModal
          isOpen={isTemplatesModalOpen}
          onClose={handleCloseTemplatesModal}
          onSubmit={handleInsertTemplateSubmit}
        />
      )}
    </>
  );
};

export default EmailReply;
